/**
 * GROQ Fragments - Centralized, DRY query fragments with cascading fallbacks
 *
 * Fallback Hierarchy (most specific → most general):
 * Twitter → OpenGraph → SEO Meta → Card → Hero → Page → Site Defaults
 *
 * Field-specific chains:
 * - Title:       twitter.title → og.title → seo.metaTitle → card.title → hero.title → title
 * - Description: twitter.description → og.description → seo.metaDescription → card.subtitle → hero.subtitle
 * - Image:       twitter.image → og.image → card.image → hero.image → hero.style.backgroundImage
 *
 * Note: Site defaults are applied at the application layer via getSiteDefaults()
 */

// =============================================================================
// BASE FRAGMENTS
// =============================================================================

/**
 * Sanity native image projection — dereferences asset with LQIP and dimensions
 */
export const SANITY_IMAGE = `
  asset->{ _id, url, metadata { lqip, dimensions { width, height, aspectRatio } } },
  hotspot, crop, alt, caption
`;

/**
 * Internal link dereferencing for CTAs
 */
export const INTERNAL_LINK = `
  internalLink->{
    _type,
    slug
  }
`;

/**
 * Sanity native video projection — dereferences the file and poster assets
 */
export const SANITY_VIDEO = `
  vidyardId,
  file { asset->{ _id, url } },
  poster { asset->{ _id, url, metadata { lqip, dimensions { width, height } } } },
  alt, width, height
`;

// =============================================================================
// SECTION STYLE + PORTABLE TEXT FRAGMENTS
// =============================================================================

/**
 * Section style with resolved background image and video.
 * Used by section modules, card grids and every portable block that has a style.
 */
export const SECTION_STYLE = `
  style {
    ...,
    backgroundImage {
      ${SANITY_IMAGE}
    },
    backgroundVideo {
      ${SANITY_VIDEO}
    }
  }
`;

/**
 * Related reference within portable text
 */
export const PORTABLE_RELATED_REFERENCE = `
  reference-> {
    _type,
    title,
    slug,
    card {
      subtitle,
      image {
        ${SANITY_IMAGE}
      }
    }
  }
`;

/** Image block inside any rich text array */
const PORTABLE_IMAGE_BLOCK = `
  _type == 'sanityImage' => {
    ...,
    ${SANITY_IMAGE}
  }
`;

/** Header CTA buttons with dereferenced internal links */
const HEADER_CTA_BUTTONS = `
  cta_buttons[] {
    ...,
    ${INTERNAL_LINK}
  }
`;

/** Members of portableTwoColumn.leftColumn / rightColumn */
const PORTABLE_TWO_COLUMN_ITEMS = `
  ...,
  ${PORTABLE_IMAGE_BLOCK},
  _type == 'portableStatistics' => {
    ...,
    ${SECTION_STYLE}
  },
  _type == 'portablePullQuote' => {
    ...,
    ${SECTION_STYLE}
  }
`;

/**
 * Rich text members that do not themselves contain rich text.
 * referenceGrid.references is deliberately NOT resolved: the component fetches it.
 */
const PORTABLE_TEXT_FLAT = `
  ${PORTABLE_IMAGE_BLOCK},
  _type == 'portableRelated' => {
    ...,
    ${PORTABLE_RELATED_REFERENCE}
  },
  _type == 'portablePullQuote' => {
    ...,
    ${SECTION_STYLE}
  },
  _type == 'portableStatistics' => {
    ...,
    ${SECTION_STYLE}
  },
  _type == 'portableKeyPoints' => {
    ...,
    ${SECTION_STYLE}
  },
  _type == 'portableGlossary' => {
    ...,
    ${SECTION_STYLE}
  },
  _type == 'portableProcessSteps' => {
    ...,
    ${SECTION_STYLE},
    steps[] {
      ...,
      content[] {
        ...,
        ${PORTABLE_IMAGE_BLOCK}
      }
    }
  },
  _type == 'portableTwoColumn' => {
    ...,
    ${SECTION_STYLE},
    leftColumn[] {
      ${PORTABLE_TWO_COLUMN_ITEMS}
    },
    rightColumn[] {
      ${PORTABLE_TWO_COLUMN_ITEMS}
    }
  }
`;

/**
 * Rich text projection. Resolves images, videos, related references and
 * internal links on the blocks of a rich text field. Inside a content block's
 * body it resolves image blocks and related references only. It goes no
 * deeper: the schema is recursive there (portableContentBlock.body,
 * header.blurb), which is why the domain type RichTextContent is opaque.
 * Resolving a full second level was measured at ten times the query size.
 */
export const PORTABLE_TEXT_EXTENSIONS = `
  ${PORTABLE_TEXT_FLAT},
  _type == 'portableContentBlock' => {
    ...,
    ${SECTION_STYLE},
    body[] {
      ...,
      ${PORTABLE_IMAGE_BLOCK},
      _type == 'portableRelated' => {
        ...,
        ${PORTABLE_RELATED_REFERENCE}
      }
    }
  },
  _type == 'header' => {
    ...,
    ${HEADER_CTA_BUTTONS}
  }
`;



// =============================================================================
// FALLBACK FRAGMENTS
// =============================================================================

/**
 * Image with cascading fallbacks
 * card.image → hero.image → hero.style.backgroundImage
 * Uses defined(*.asset) to skip images without an uploaded asset
 */
export const IMAGE_WITH_FALLBACKS = `
  coalesce(
    select(defined(card.image.asset) => card.image),
    select(defined(hero.image.asset) => hero.image),
    select(defined(hero.style.backgroundImage.asset) => hero.style.backgroundImage)
  ) {
    ${SANITY_IMAGE}
  }
`;

/**
 * Title with cascading fallbacks
 * card.title → hero.title → title
 */
export const TITLE_WITH_FALLBACKS = `coalesce(card.title, hero.title, title)`;

/**
 * Subtitle/description with cascading fallbacks
 * card.subtitle → hero.subtitle → seo.metaDescription
 */
export const SUBTITLE_WITH_FALLBACKS = `coalesce(card.subtitle, hero.subtitle, seo.metaDescription)`;

// =============================================================================
// COMPOSITE FRAGMENTS
// =============================================================================

/**
 * Card projection with full fallback chain
 * Used for listing pages, related content, reference grids
 */
export const CARD_PROJECTION = `
  "card": {
    "title": ${TITLE_WITH_FALLBACKS},
    "subtitle": ${SUBTITLE_WITH_FALLBACKS},
    "cardBlurb": card.cardBlurb[] { ..., ${PORTABLE_TEXT_EXTENSIONS} },
    "image": ${IMAGE_WITH_FALLBACKS},
    // Raw check for explicit card image (no fallback) - used for gradient treatment
    "hasExplicitImage": defined(card.image.asset)
  }
`;

/**
 * SEO projection with OpenGraph and Twitter fallbacks
 *
 * Fallback chain: Twitter → OG → SEO → Card → Hero → Page
 * Site defaults are applied at the application layer
 */
export const SEO_WITH_FALLBACKS = `
  "seo": {
    ...seo,
    "additionalMetaTags": seo.additionalMetaTags[] {
      ...,
      metaAttributes[] {
        ...,
        attributeValueImage { ${SANITY_IMAGE} }
      }
    },
    "metaTitle": coalesce(seo.metaTitle, card.title, hero.title, title),
    "metaDescription": coalesce(seo.metaDescription, card.subtitle, hero.subtitle),
    "openGraph": {
      ...seo.openGraph,
      "title": coalesce(seo.openGraph.title, seo.metaTitle, card.title, hero.title, title),
      "description": coalesce(seo.openGraph.description, seo.metaDescription, card.subtitle, hero.subtitle),
      "image": coalesce(
        select(defined(seo.openGraph.image.asset) => seo.openGraph.image),
        select(defined(card.image.asset) => card.image),
        select(defined(hero.image.asset) => hero.image),
        select(defined(hero.style.backgroundImage.asset) => hero.style.backgroundImage)
      ) {
        ${SANITY_IMAGE}
      }
    },
    "twitter": {
      ...seo.twitter,
      "title": coalesce(seo.twitter.title, seo.openGraph.title, seo.metaTitle, card.title, hero.title, title),
      "description": coalesce(seo.twitter.description, seo.openGraph.description, seo.metaDescription, card.subtitle, hero.subtitle),
      "image": coalesce(
        select(defined(seo.twitter.image.asset) => seo.twitter.image),
        select(defined(seo.openGraph.image.asset) => seo.openGraph.image),
        select(defined(card.image.asset) => card.image),
        select(defined(hero.image.asset) => hero.image),
        select(defined(hero.style.backgroundImage.asset) => hero.style.backgroundImage)
      ) {
        ${SANITY_IMAGE}
      }
    }
  }
`;

/**
 * Minimal card for listing items (lighter query)
 */
export const LISTING_ITEM = `
  _id,
  _type,
  title,
  slug,
  ${CARD_PROJECTION}
`;

/**
 * Related content item (for article/whitepaper sidebars)
 */
export const RELATED_ITEM = `
  _id,
  _type,
  slug,
  "title": ${TITLE_WITH_FALLBACKS},
  "subtitle": ${SUBTITLE_WITH_FALLBACKS},
  "image": ${IMAGE_WITH_FALLBACKS}
`;

// =============================================================================
// HERO FRAGMENT
// =============================================================================

/**
 * Hero section with all image/video handling
 */
export const HERO_FRAGMENT = `
  hero {
    _type,
    variant,
    title,
    titleLine2,
    subtitle,
    ctas[] {
      ...,
      actions[] {
        ...,
        ${INTERNAL_LINK}
      },
      image {
        ${SANITY_IMAGE}
      },
      logos[] {
        ...,
        ${SANITY_IMAGE}
      }
    },
    image {
      ${SANITY_IMAGE}
    },
    // HeroLogoGradient fields
    gradientColors,
    heroLogo {
      ${SANITY_IMAGE}
    },
    // HeroForm fields
    formFillType,
    formPurpose,
    formHeading,
    formDescription,
    formSubmitLabel,
    formSuccessHeading,
    formSuccessMessage,
    formErrorMessage,
    style {
      backgroundColor,
      backgroundImage {
        ${SANITY_IMAGE}
      },
      backgroundVideo {
        ${SANITY_VIDEO}
      },
      backgroundPattern,
      patternOpacity,
      customClasses,
      containerWidth,
      paddingTop,
      paddingBottom
    }
  }
`;

// =============================================================================
// CONTENT MODULE FRAGMENTS
// =============================================================================

/**
 * Reference grid with dereferenced items and fallbacks
 */
export const REFERENCE_GRID_CONTENT = `
  _type == 'referenceGrid' => {
    ...,
    references[]-> {
      _id,
      _type,
      _createdAt,
      "slug": slug,
      "title": ${TITLE_WITH_FALLBACKS},
      "image": ${IMAGE_WITH_FALLBACKS},
      "subtitle": ${SUBTITLE_WITH_FALLBACKS},
      "hasExplicitImage": defined(card.image.asset),
      featured,
      publishedAt,
      sortOrder,
      orderRank,
      "heroGradient": select(
        hero.variant == "heroLogoGradient" => {
          "variant": hero.variant,
          "gradientColors": hero.gradientColors,
          "heroLogo": hero.heroLogo { ${SANITY_IMAGE} }
        },
        null
      )
    }
  }
`;

/**
 * Image grid with Sanity image handling
 */
export const IMAGE_GRID_CONTENT = `
  _type == 'imageGrid' => {
    ...,
    items[] {
      ...,
      image {
        ${SANITY_IMAGE}
      }
    }
  }
`;

/**
 * Team grid with member images
 */
export const TEAM_GRID_CONTENT = `
  _type == 'teamGrid' => {
    ...,
    members[]-> {
      ...,
      "image": image {
        ${SANITY_IMAGE}
      },
      bio[] {
        ...,
        ${PORTABLE_TEXT_EXTENSIONS}
      }
    }
  }
`;

/**
 * Rich text with portable extensions
 */
export const RICH_TEXT_CONTENT = `
  _type == 'richText' => {
    content[] {
      ...,
      ${PORTABLE_TEXT_EXTENSIONS}
    }
  }
`;

/**
 * Split text with portable extensions in the body column
 */
export const SPLIT_TEXT_CONTENT = `
  _type == 'splitText' => {
    ...,
    body[] {
      ...,
      ${PORTABLE_TEXT_EXTENSIONS}
    }
  }
`;

/**
 * Header with CTA buttons
 */
export const HEADER_CONTENT = `
  _type == 'header' => {
    ...,
    ${HEADER_CTA_BUTTONS},
    blurb[] {
      ...,
      ${PORTABLE_TEXT_EXTENSIONS}
    }
  }
`;

/**
 * CTA Block with dereferenced internal links
 */
export const CTA_BLOCK_CONTENT = `
  _type == 'ctaBlock' => {
    ...,
    actions[] {
      ...,
      ${INTERNAL_LINK}
    },
    image {
      ${SANITY_IMAGE}
    },
    logos[] {
      ...,
      ${SANITY_IMAGE}
    }
  }
`;

/**
 * Card grid with resolved section style.
 * items[] stay stored references: no component renders them.
 */
export const CARD_GRID_CONTENT = `
  _type == 'cardGrid' => {
    ...,
    ${SECTION_STYLE}
  }
`;

/**
 * Bordered grid with resolved brand image
 */
export const BORDERED_GRID_CONTENT = `
  _type == 'borderedGrid' => {
    ...,
    brandImage {
      ${SANITY_IMAGE}
    }
  }
`;

/**
 * Contact form with resolved rich text body
 */
export const CONTACT_FORM_CONTENT = `
  _type == 'contactForm' => {
    ...,
    body[] {
      ...,
      ${PORTABLE_TEXT_EXTENSIONS}
    }
  }
`;

/**
 * Content Promo - passes through reference for component to fetch
 * Note: Do NOT dereference promotedItem here - the ContentPromo component
 * expects promotedItem._ref and fetches the data itself
 */
export const CONTENT_PROMO_CONTENT = `
  _type == 'contentPromo' => {
    ...
  }
`;

/**
 * All section module content types combined
 */
export const SECTION_MODULE_CONTENT = `
  _type == 'sectionModule' => {
    ...,
    ${SECTION_STYLE},
    content[] {
      ...,
      ${HEADER_CONTENT},
      ${REFERENCE_GRID_CONTENT},
      ${IMAGE_GRID_CONTENT},
      ${RICH_TEXT_CONTENT},
      ${TEAM_GRID_CONTENT},
      ${CTA_BLOCK_CONTENT},
      ${CONTENT_PROMO_CONTENT},
      ${SPLIT_TEXT_CONTENT},
      ${CARD_GRID_CONTENT},
      ${BORDERED_GRID_CONTENT},
      ${CONTACT_FORM_CONTENT}
    }
  }
`;

// =============================================================================
// TESTIMONIAL FRAGMENT
// =============================================================================

/**
 * Testimonial projection — quote and author attribution with headshot
 */
export const TESTIMONIAL = `
  quote, authorName, authorTitle, authorCompany, authorImage { ${SANITY_IMAGE} }
`;

// =============================================================================
// AUTHOR FRAGMENT
// =============================================================================

/**
 * Author with image
 */
export const AUTHOR_FRAGMENT = `
  "author": author-> {
    ...,
    "image": image {
      ${SANITY_IMAGE}
    },
    bio[] {
      ...,
      ${PORTABLE_TEXT_EXTENSIONS}
    }
  }
`;

// =============================================================================
// DOCUMENT TYPE FRAGMENTS
// =============================================================================

/**
 * Case Study specific fields
 */
export const CASE_STUDY_FIELDS = `
  _type == "caseStudy" => {
    hero {
      ...,
      "variant": "heroCaseStudy",
      image {
        ${SANITY_IMAGE}
      }
    },
    client->{ name, logo { ${SANITY_IMAGE} } },
    customerName,
    // Resolved References (industries/partners/services/solutions) for JSON-LD
    // mentions; the raw relatedInformation refs stay untouched for other consumers
    "relatedEntities": relatedInformation[]->{ _type, title, "slug": slug.current },
    ${CARD_PROJECTION},
    overview[] {
      ...,
      ${PORTABLE_TEXT_EXTENSIONS}
    },
    overviewImage {
      ${SANITY_IMAGE}
    },
    narrative {
      challenge {
        body[] { ..., ${PORTABLE_TEXT_EXTENSIONS} },
        image { ${SANITY_IMAGE} }
      },
      solution {
        body[] { ..., ${PORTABLE_TEXT_EXTENSIONS} },
        image { ${SANITY_IMAGE} }
      },
      results {
        body[] { ..., ${PORTABLE_TEXT_EXTENSIONS} },
        image { ${SANITY_IMAGE} }
      }
    },
    stats {
      heading,
      subheading,
      items[] {
        ...,
        icon {
          ${SANITY_IMAGE}
        }
      }
    },
    approach {
      heading,
      intro[] { ..., ${PORTABLE_TEXT_EXTENSIONS} },
      layers[] {
        _key,
        label,
        title,
        body[] { ..., ${PORTABLE_TEXT_EXTENSIONS} },
        image { ${SANITY_IMAGE} }
      }
    },
    outcomes {
      heading,
      intro[] { ..., ${PORTABLE_TEXT_EXTENSIONS} },
      blocks[] {
        _key,
        eyebrow,
        heading,
        body[] { ..., ${PORTABLE_TEXT_EXTENSIONS} },
        image { ${SANITY_IMAGE} },
        metrics[] { ..., icon { ${SANITY_IMAGE} } }
      }
    },
    testimonial {
      quote,
      author,
      authorTitle,
      portrait { ${SANITY_IMAGE} }
    },
    closing {
      heading,
      body[] { ..., ${PORTABLE_TEXT_EXTENSIONS} },
      cta {
        ...,
        ${INTERNAL_LINK}
      }
    }
  }
`;

/**
 * Article specific fields
 */
export const ARTICLE_FIELDS = (excludeSlug: string = '$slug') => `
  _type == "article" => {
    hero {
      ...,
      image {
        ${SANITY_IMAGE}
      }
    },
    ${AUTHOR_FRAGMENT},
    category,
    body[] {
      ...,
      ${PORTABLE_TEXT_EXTENSIONS}
    },
    "relatedInsights": *[_type in ["article", "whitepaper"] && slug.current != ${excludeSlug}] | order(coalesce(publishedAt, _createdAt) desc)[0...4] {
      ${RELATED_ITEM}
    }
  }
`;

/**
 * Whitepaper specific fields
 */
export const WHITEPAPER_FIELDS = (excludeSlug: string = '$slug') => `
  _type == "whitepaper" => {
    hero {
      ...,
      title,
      subtitle,
      image {
        ${SANITY_IMAGE}
      }
    },
    category,
    salesforceCampaignIdOverride,
    sections[] {
      ...,
      body[] {
        ...,
        ${PORTABLE_TEXT_EXTENSIONS}
      }
    },
    "relatedInsights": *[_type in ["article", "whitepaper"] && slug.current != ${excludeSlug}] | order(coalesce(publishedAt, _createdAt) desc)[0...4] {
      ${RELATED_ITEM}
    }
  }
`;
